import React, { useState, useContext, useEffect, useCallback, useMemo } from "react";
// Services
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

// Context
import { AuthContext } from "../../context/Auth/AuthContext";

// Material UI Components
import {
  alpha,
  styled,
  useTheme,
  Box,
  Divider,
  Typography,
  Stack,
  MenuItem,
  Avatar,
  Slider,
  Tooltip,
  Button,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';

// Icons
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeDown from '@mui/icons-material/VolumeDown';
import VolumeUp from '@mui/icons-material/VolumeUp';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import PersonIcon from '@mui/icons-material/Person';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import DoNotDisturbOnIcon from '@mui/icons-material/DoNotDisturbOn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// Custom Components
import MenuPopover from '../../components/MenuPopover';
import { IconButtonAnimate } from '../../components/animate';
import AvatarPresence from "../../components/AvatarPresence";
import socket from "../../hooks/useSocket";
import { getPresence, isBusy } from "../../helpers/presence";
import ToastError from "../../toast/error/toastError";
import CreateOrEditUserModal from "../../components/CreateOrEditUserModal";
import ChangeNotificationsModal from "../../components/ChangeNotificationsModal";

// Styled Components
const MenuItemStyled = styled(MenuItem)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(1.5, 2.5),
  borderRadius: theme.shape.borderRadius,
  transition: theme.transitions.create('background-color'),
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
}));

const MenuLabel = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  marginRight: theme.spacing(1),
}));

const MenuIconWrapper = styled(Box)(({ theme }) => ({
  color: theme.palette.primary.main,
  display: 'flex',
  alignItems: 'center',
}));

const UserInfoBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  width: '100%',
}));

const VolumeControl = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  width: '100%',
}));

const VolumeIconButton = styled(Box)(({ theme }) => ({
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(0.5),
  borderRadius: '50%',
  transition: theme.transitions.create('background-color'),
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
}));

/**
 * AccountPopover - User account menu component with volume controls and profile options
 */
export default function AccountPopover() {
  const theme = useTheme();
  const { user, status, statusMessage, handleLogout, handleUpdateUser } = useContext(AuthContext);

  // States
  const [anchorEl, setAnchorEl] = useState(null);
  const [busyOpen, setBusyOpen] = useState(false);
  const [reason, setReason] = useState("");
  const busy = isBusy(status);
  const [volume, setVolume] = useState(() => {
    return Number(localStorage.getItem("volume") || 50);
  });
  const [modals, setModals] = useState({
    user: false,
    notifications: false
  });

  // Check if the menu is open
  const isMenuOpen = Boolean(anchorEl);

  // Update local storage when user notifications settings change
  useEffect(() => {
    if (user) {
      localStorage.setItem("globalNotification", user.globalNotification);
      localStorage.setItem("userNotification", user.userNotification);
    }
  }, [user?.globalNotification, user?.userNotification]);

  // Menu handlers
  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  // Modal handlers
  const toggleModal = (modalName, state) => {
    setModals(prev => ({ ...prev, [modalName]: state }));
    if (state === false) {
      handleCloseMenu();
    }
  };

  // Volume handlers
  const handleVolumeChange = useCallback((event, newValue) => {
    localStorage.setItem("volume", newValue);
    setVolume(newValue);
  }, []);

  const toggleMute = useCallback(() => {
    const newVolume = volume > 0 ? 0 : 100;
    handleVolumeChange(null, newVolume);
  }, [volume, handleVolumeChange]);

  // Notification settings handler
  const updateNotificationSettings = async ({ selectedAudioGlobal, selectedAudioMy }) => {
    try {
      // Update on server
      await api.put(`/users/${user.id}`, {
        globalNotification: selectedAudioGlobal,
        userNotification: selectedAudioMy
      });

      // Update local state
      handleUpdateUser({
        ...user,
        globalNotification: selectedAudioGlobal,
        userNotification: selectedAudioMy
      });

      // Update local storage
      localStorage.setItem("globalNotification", selectedAudioGlobal);
      localStorage.setItem("userNotification", selectedAudioMy);
    } catch (err) {
      console.error("Failed to update notification settings:", err);
    } finally {
      toggleModal('notifications', false);
    }
  };

  // Memoized volume icon rendering
  const VolumeIcon = useMemo(() => {
    const handleClick = () => toggleMute();

    let Icon = VolumeUp;
    let color = 'inherit';

    if (volume === 0) {
      Icon = VolumeOffIcon;
    } else if (volume < 50) {
      Icon = VolumeDown;
    } else if (volume === 100) {
      color = theme.palette.primary.main;
    }

    return (
      <VolumeIconButton onClick={handleClick}>
        <Tooltip title={volume === 0 ? "Ativar som" : "Silenciar"}>
          <Icon sx={{ color }} />
        </Tooltip>
      </VolumeIconButton>
    );
  }, [volume, toggleMute, theme]);

  // Handle logout
  const handleUserLogout = () => {
    handleCloseMenu();
    handleLogout();
  };

  // Presença: ocupado / disponível
  const openBusyDialog = () => {
    handleCloseMenu();
    setReason(busy ? (statusMessage || "") : "");
    setBusyOpen(true);
  };

  const confirmBusy = () => {
    const message = reason.trim();
    if (!message) return;
    // Backend usa socket.userId (registrado no layout) — não precisa userId.
    // Ack confirma; só fecha o diálogo no sucesso.
    socket.emit("set_busy", { message }, (resp) => {
      if (resp?.ok) setBusyOpen(false);
      else ToastError("Não foi possível marcar como ocupado. Tente novamente.");
    });
  };

  const setAvailable = () => {
    handleCloseMenu();
    socket.emit("set_available", {}, (resp) => {
      if (!resp?.ok) ToastError("Não foi possível voltar a disponível. Tente novamente.");
    });
  };

  const BUSY_PRESETS = ["Almoço", "Banheiro", "Reunião", "Café", "Pausa"];

  // Check if user data exists
  if (!user) return null;

  return (
    <>
      {/* User Profile Modal */}
      <CreateOrEditUserModal
        open={modals.user}
        onClose={() => toggleModal('user', false)}
        userId={user.id}
      />

      {/* Notification Settings Modal */}
      <ChangeNotificationsModal
        open={modals.notifications}
        onClose={() => toggleModal('notifications', false)}
        globalNotification={user.globalNotification}
        userNotification={user.userNotification}
        onConfirm={updateNotificationSettings}
      />

      {/* Avatar Button */}
      <Tooltip title="Perfil do Usuário">
        <IconButtonAnimate
          onClick={handleOpenMenu}
          aria-label="Abrir menu de perfil"
          aria-controls={isMenuOpen ? 'account-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={isMenuOpen ? 'true' : undefined}
          sx={{
            p: 0,
            ...(isMenuOpen && {
              '&:before': {
                zIndex: 1,
                content: "''",
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                position: 'absolute',
                bgcolor: (theme) => alpha(theme.palette.grey[900], 0.8),
              },
            }),
          }}
        >
          <AvatarPresence
            id="user-status-badge"
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            variant="dot"
            status={status}
            title={busy ? `Ocupado${statusMessage ? `: ${statusMessage}` : ''}` : undefined}
          >
            <Avatar
              alt={user.name}
              src={user?.media || user?.medias?.mediaUrl || user?.driveUrl}
              sx={{ width: 40, height: 40 }}
            />
          </AvatarPresence>
        </IconButtonAnimate>
      </Tooltip>

      {/* Menu Popover */}
      <MenuPopover
        id="account-menu"
        open={isMenuOpen}
        anchorEl={anchorEl}
        onClose={handleCloseMenu}
        sx={{
          width: 320,
          p: 0,
          mt: 1.5,
          ml: 0.75,
          '& .MuiMenuItem-root': {
            typography: 'body2',
            borderRadius: 0.75,
          },
        }}
      >
        {/* User Info Section */}
        <UserInfoBox>
          <Typography variant="subtitle1" fontWeight="bold" noWrap>
            {user.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {user.email}
          </Typography>
        </UserInfoBox>

        <Divider sx={{ borderStyle: 'dashed' }} />

        {/* Sound Settings */}
        <MenuItemStyled onClick={() => toggleModal('notifications', true)}>
          <MenuLabel variant="body2">Sons de Notificação</MenuLabel>
          <MenuIconWrapper>
            <AudiotrackIcon />
          </MenuIconWrapper>
        </MenuItemStyled>

        {/* Volume Control */}
        <VolumeControl>
          <Stack spacing={2} direction="row" alignItems="center">
            <Slider
              aria-label="Volume"
              value={volume}
              onChange={handleVolumeChange}
              color="primary"
              size="small"
              sx={{ flex: 1 }}
            />
            {VolumeIcon}
          </Stack>
        </VolumeControl>

        <Divider sx={{ borderStyle: 'dashed', my: 1 }} />

        {/* Presença: marcar/desmarcar ocupado */}
        {busy ? (
          <>
            <Box sx={{ px: 2, pt: 0.5, pb: 0.5 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: getPresence(status).color }} />
                <Typography variant="body2" fontWeight={700}>Ocupado</Typography>
              </Stack>
              {statusMessage && (
                <Typography variant="caption" color="text.secondary" sx={{ pl: 2.5 }} noWrap display="block">
                  {statusMessage}
                </Typography>
              )}
            </Box>
            <MenuItemStyled onClick={setAvailable}>
              <MenuLabel variant="body2">Ficar disponível</MenuLabel>
              <MenuIconWrapper sx={{ color: 'success.main' }}><CheckCircleIcon /></MenuIconWrapper>
            </MenuItemStyled>
          </>
        ) : (
          <MenuItemStyled onClick={openBusyDialog}>
            <MenuLabel variant="body2">Marcar como ocupado</MenuLabel>
            <MenuIconWrapper sx={{ color: getPresence(3).color }}><DoNotDisturbOnIcon /></MenuIconWrapper>
          </MenuItemStyled>
        )}

        <Divider sx={{ borderStyle: 'dashed', my: 1 }} />

        {/* Profile and Logout Options */}
        <MenuItemStyled onClick={() => toggleModal('user', true)}>
          <MenuLabel variant="body2">{i18n.t("mainDrawer.appBar.user.profile")}</MenuLabel>
          <MenuIconWrapper>
            <PersonIcon />
          </MenuIconWrapper>
        </MenuItemStyled>

        <MenuItemStyled onClick={handleUserLogout}>
          <MenuLabel variant="body2">{i18n.t("mainDrawer.appBar.user.logout")}</MenuLabel>
          <MenuIconWrapper>
            <ExitToAppIcon />
          </MenuIconWrapper>
        </MenuItemStyled>
      </MenuPopover>

      {/* Diálogo: motivo do "ocupado" (obrigatório) */}
      <Dialog open={busyOpen} onClose={() => setBusyOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Marcar como ocupado</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Diga o que você está fazendo (almoço, banheiro, reunião…). Enquanto ocupado, o
            status automático fica pausado.
          </Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {BUSY_PRESETS.map((p) => (
              <Chip
                key={p}
                label={p}
                size="small"
                onClick={() => setReason(p)}
                color={reason === p ? 'primary' : 'default'}
                variant={reason === p ? 'filled' : 'outlined'}
              />
            ))}
          </Stack>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Motivo"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') confirmBusy(); }}
            inputProps={{ maxLength: 255 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setBusyOpen(false)} color="inherit">Cancelar</Button>
          <Button onClick={confirmBusy} variant="contained" disabled={!reason.trim()}>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}